import React from "react";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { Chip } from "@components/chip";
import { IFilterOption, TErrorMessages } from "../filterModal.types";
import { Radio } from "../../radio";
import {
    chipFilterItemClass,
    StyledChipFilterWrapper,
    StyledChipGroup,
} from "../filterModal.styles";
import { FILTER_MODAL_DATA_IDS } from "../filterModal.constants";

export interface IRadioFiltersProps<
    T extends Record<keyof T, IFilterOption[]>,
> {
    isSingleFilterMode: boolean;
    filterOptions: IFilterOption[];
    selectedFilterType: keyof T;
    selectedFilters: Partial<T>;
    errorMessage?: Partial<TErrorMessages<T>>;
    setSelectedFilters: (selectedFilters: Partial<T>) => void;
}

const RadioFilters = <T extends Record<keyof T, IFilterOption[]>>(
    props: IRadioFiltersProps<T>
) => {
    const {
        isSingleFilterMode,
        filterOptions,
        selectedFilters,
        errorMessage,
        setSelectedFilters,
        selectedFilterType,
    } = props;
    const tokens = useEnigmaToken();
    const onChipChange = (filterItem: IFilterOption) => {
        const updatedFilters = { ...selectedFilters };
        let updatedFilterOptions: IFilterOption[] = [];
        updatedFilterOptions = [filterItem];

        if (isSingleFilterMode) {
            const newLocalFilters = {
                [selectedFilterType]: updatedFilterOptions,
            } as Partial<T>;
            setSelectedFilters(newLocalFilters);
        } else {
            updatedFilters[selectedFilterType] =
                updatedFilterOptions as T[keyof T];
            setSelectedFilters(updatedFilters);
        }
    };

    return (
        <StyledChipFilterWrapper
            tokens={tokens}
            hasError={Boolean(errorMessage?.[selectedFilterType])}
        >
            <StyledChipGroup data-testid={FILTER_MODAL_DATA_IDS.RADIO_GROUP}>
                {filterOptions.map((filterItem) => {
                    const _isSelected = selectedFilters[selectedFilterType]
                        ?.map((opt) => opt.value)
                        ?.includes(filterItem.value);
                    return (
                        <Chip
                            data-testid={FILTER_MODAL_DATA_IDS.RADIO_OPTION}
                            selectableWhenSuffix
                            key={filterItem.key}
                            selected={_isSelected}
                            size="large"
                            className={chipFilterItemClass(tokens)}
                            suffix={
                                <Radio
                                    data-testid={
                                        FILTER_MODAL_DATA_IDS.RADIO_INPUT
                                    }
                                    style={{ marginInlineEnd: 0 }}
                                    onClick={() => onChipChange(filterItem)}
                                    disabled={Boolean(filterItem.isDisabled)}
                                    checked={_isSelected}
                                    value={filterItem.value}
                                />
                            }
                            onClick={() => onChipChange(filterItem)}
                            disabled={Boolean(filterItem.isDisabled)}
                        >
                            {filterItem.title}
                        </Chip>
                    );
                })}
            </StyledChipGroup>
        </StyledChipFilterWrapper>
    );
};

export default RadioFilters;
